import {Injectable} from "@angular/core";
import {Action, createSelector, Selector, State, StateContext} from "@ngxs/store";
import {CompoTreeType} from "../../enums/CompoTreeType";
import {OrganizationLocationTreeActions} from "./organization-location-tree.actions";
import SetYIndexFor = OrganizationLocationTreeActions.SetYIndexFor;
import RemoveYIndexFor = OrganizationLocationTreeActions.RemoveYIndexFor;
import OnParentSelected = OrganizationLocationTreeActions.OnParentSelected;
import SetSelection = OrganizationLocationTreeActions.SetSelection;
import SetAllLocationIds = OrganizationLocationTreeActions.SetAllLocationIds;
import SetAllOrganizationIds = OrganizationLocationTreeActions.SetAllOrganizationIds;
import {ResetStateDefaults} from "../base-action-class.model";

export class OrganizationLocationTreeStateModel {
  yIndexes: { id: string, parentId: string, type: CompoTreeType, yIndex: number }[] = [];
  allLocationsIds: Set<string>;
  allOrganizationIds: Set<string>;
  selectedLocationIds: Set<string>;
  selectedOrganizationIds: Set<string>;
}

const getDefaults = (): OrganizationLocationTreeStateModel => ({
  yIndexes: [],
  allLocationsIds: new Set<string>(),
  allOrganizationIds: new Set<string>(),
  selectedLocationIds: new Set<string>(),
  selectedOrganizationIds: new Set<string>(),
});

@State<OrganizationLocationTreeStateModel>({
  name: 'organizationLocationTree',
  defaults: getDefaults()
})
@Injectable()
export class OrganizationLocationTreeXsState {

  //region SELECTORS
  // @ts-ignore
  @Selector()
  static getIndex(id: string, type: CompoTreeType, searchTerm: string) {
    return createSelector([this], (state): number => {
      const yIndexes = [...state.organizationLocationTree.yIndexes];
      const indexOfEntry = yIndexes.findIndex(yIndex => yIndex.id === id && yIndex.type === type);
      if (indexOfEntry !== -1) {
        return yIndexes
          .slice(0, indexOfEntry + 1)
          .map(yIndex => yIndex.yIndex)
          .reduce((prev, curr) => prev + curr, 0);
      }
      return 0;
    });
  }

  @Selector()
  static selectedLocationIds(state: OrganizationLocationTreeStateModel): Set<string> {
    return state.selectedLocationIds;
  }

  @Selector()
  static selectedOrganizationIds(state: OrganizationLocationTreeStateModel): Set<string> {
    return state.selectedOrganizationIds;
  }

  @Selector()
  static allLocationIds(state: OrganizationLocationTreeStateModel): Set<string> {
    return state.allLocationsIds;
  }

  @Selector()
  static allOrganizationIds(state: OrganizationLocationTreeStateModel): Set<string> {
    return state.allOrganizationIds;
  }
  //endregion SELECTORS

  //region ACTIONS
  @Action(SetYIndexFor)
  public setYIndexFor(ctx: StateContext<OrganizationLocationTreeStateModel>, action: SetYIndexFor) {
    const id = action.id, parentId = action.parentId, type = action.type,
      yIndexes = [...ctx.getState().yIndexes],
      yIndex = yIndexes.find(yIndex => yIndex.id === id && yIndex.type === type);
    let index = action.index;

    if (index === -1) {
      index = null;
    }

    if (yIndex) {
      const childIndex = yIndexes.findIndex(e => e.id === id && e.type === type);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 1, {id, parentId, type, yIndex: index});
      } else {
        yIndexes.push({id, type, parentId, yIndex: index});
      }
    } else {
      const childIndex = yIndexes.findIndex(e => e.parentId === id && e.type === type);
      if (childIndex !== -1) {
        yIndexes.splice(childIndex, 0, {id, parentId, type: type, yIndex: index});
      } else {
        yIndexes.push({id, type, parentId, yIndex: index});
      }
    }

    ctx.patchState({
      yIndexes: yIndexes
    });
  }

  @Action(RemoveYIndexFor)
  public removeYIndexFor(ctx: StateContext<OrganizationLocationTreeStateModel>, action: RemoveYIndexFor) {
    let yIndexes = [...ctx.getState().yIndexes];
    const id = action.id, type = action.type,
      yIndexIdx = yIndexes.findIndex(yIndex => yIndex.id === id && yIndex.type === type);


    if (yIndexIdx !== -1) {
      yIndexes = yIndexes.slice(0, yIndexIdx);
    }

    ctx.patchState({
      yIndexes: yIndexes
    });
  }

  //region selectedParentChild
  @Action(OnParentSelected)
  public onParentSelected(ctx: StateContext<OrganizationLocationTreeStateModel>, action: OnParentSelected) {
    //currently no implementation
  }
  //endregion selectedParentChild

  //region selection
  @Action(SetSelection)
  public setSelection(ctx: StateContext<OrganizationLocationTreeStateModel>, action: SetSelection) {
    ctx.patchState({
      selectedOrganizationIds: action.organizationIds,
      selectedLocationIds: action.locationIds
    });
  }

  @Action(SetAllOrganizationIds)
  public setAllOrganizationIds(ctx: StateContext<OrganizationLocationTreeStateModel>, action: SetAllOrganizationIds) {
   ctx.patchState({
     allOrganizationIds: action.organizationIds
   });
  }

  @Action(SetAllLocationIds)
  public setAllLocationIds(ctx: StateContext<OrganizationLocationTreeStateModel>, action: SetAllLocationIds) {
    ctx.patchState({
      allLocationsIds: action.locationIds
    });
  }
  //endregion selection

  //region reset
  @Action(ResetStateDefaults)
  public resetDefaults(ctx: StateContext<OrganizationLocationTreeStateModel>) {
    ctx.setState({...getDefaults()});
  }
  //endregion reset

  //endregion ACTIONS
}
